import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { createTenantLead } from '../../services/crmService'; 
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../../services/firebase';

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", 
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", 
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", 
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", 
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function GenesisAccountCreator({ onClose, isOperatorView = false }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth(); 
  const { tenantId, loading: tenantLoading } = useTenant(); 

  const [formData, setFormData] = useState({
    entityName: '', dba: '', ein: '', billingStreet: '', billingCity: '', billingState: 'WA', billingZip: '',
    entityRole: '', industryType: '', customIndustryType: '', contactName: '', contactTitle: '',
    contactEmail: '', contactPhone: '', billingEmail: '', techEmail: '', legacySystem: '',
    goLiveDate: '', packageType: 'apex_os', 
    customMrrOverride: '' // Allows reps to negotiate standard MRR
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successType, setSuccessType] = useState(null); // 'DEAL_DESK' | 'PROVISIONED' | null
  const [isConfirmed, setIsConfirmed] = useState(false);

  // --- DYNAMIC GLOBAL PRICING STATE ---
  const [globalMargins, setGlobalMargins] = useState({ monthly_door_fee: 249 });

  useEffect(() => {
    // Listen to the Founders Control Plane settings so the Base Door Fee is never hardcoded
    const unsub = onSnapshot(doc(db, 'system_config', 'global_settings'), (snap) => {
      if (snap.exists() && snap.data().pricing_margins) {
        setGlobalMargins(snap.data().pricing_margins);
      }
    });
    return () => unsub();
  }, []);

  // --- THE NEW CORPORATE PRICING STACK ---
  const OPERATIONS_HUB_PRICE = 149; 
  const BASE_DOOR_FEE = globalMargins.monthly_door_fee || 249;
  const CHECKHQ_BASE_FEE = 29;
  const INITIAL_SEAT_FEE = 5;
  
  // Bundled Apex Suite MRR ($283 standard)
  const APEX_SUITE_PRICE = BASE_DOOR_FEE + CHECKHQ_BASE_FEE + INITIAL_SEAT_FEE; 

  const calculateMrr = () => {
    if (formData.customMrrOverride !== '') {
      return Number(formData.customMrrOverride);
    }
    return formData.packageType === 'apex_os' ? APEX_SUITE_PRICE : OPERATIONS_HUB_PRICE;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'radio') {
      setFormData(prev => ({ ...prev, packageType: value, customMrrOverride: '' })); 
    } else if (name === 'ein') {
      let formatted = value.replace(/\D/g, ''); 
      if (formatted.length > 2) {
        formatted = `${formatted.substring(0, 2)}-${formatted.substring(2, 9)}`;
      }
      setFormData(prev => ({ ...prev, ein: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    else navigate(-1); 
  };

  const mapSystemRole = (role) => {
    if (role === 'Master Agent') return 'MASTER_AGENT';
    return 'CLIENT_OWNER'; 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tenantId) {
      alert("System Error: Corporate identity not resolved. Cannot provision account.");
      return;
    }

    if (formData.ein.length !== 10) {
      alert("Please ensure the EIN is exactly 9 digits.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const calculatedMrrValue = calculateMrr();
      const repUid = currentUser?.uid || 'UNKNOWN_UID'; 
      const repName = currentUser?.email?.split('@')[0] || 'Unknown Rep';
      const mappedSystemRole = mapSystemRole(formData.entityRole);
      const isMasterAgent = formData.entityRole === 'Master Agent';
      
      const assignedHomeStoreId = 'store_corporate_reinhardt';
      const fullBillingAddress = `${formData.billingStreet}, ${formData.billingCity}, ${formData.billingState} ${formData.billingZip}`;
      const pendingTenantId = `TN-${Date.now().toString().slice(-6)}-${Math.floor(1000 + Math.random() * 9000)}`;

      const basePayload = {
        company_name: formData.entityName,
        dba: formData.dba,
        ein: formData.ein,
        billingAddress: fullBillingAddress,
        entityRole: formData.entityRole,
        systemRole: mappedSystemRole, 
        homeStoreId: assignedHomeStoreId, 
        industryType: formData.industryType === 'Other' ? formData.customIndustryType : formData.industryType,
        contact_name: formData.contactName,
        contactTitle: formData.contactTitle,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        billingEmail: formData.billingEmail,
        techEmail: formData.techEmail,
        mrr_value: calculatedMrrValue, 
        mrr: calculatedMrrValue,       
        calculatedOneTime: 0, // No longer collected at minting
        amountPaid: 0,
        totalDueToday: 0, 
        packageType: formData.packageType,
        services: {
          crm: false, customWebsite: false, managedHosting: false, initialAdSpend: 0,
          consultingFee: 0, onSiteTrainingFee: 0, playbooksFee: 0, dirtToRibbonFee: 0
        },
        legacySystem: formData.legacySystem,
        goLiveDate: formData.goLiveDate,
        rep_uid: repUid,
        rep_name: repName,
        target_tenant_id: pendingTenantId
      };

      // ==========================================
      // LOGIC FORK 1: MASTER AGENT -> DEAL DESK
      // ==========================================
      if (isMasterAgent) {
        const dealDeskPayload = {
          ...basePayload,
          stage: 'closedWon', 
          status: 'Deal Desk Review',
          paymentStatus: 'pending_review'
        };

        await createTenantLead(tenantId, dealDeskPayload);
        setSuccessType('DEAL_DESK');
      } 
      
      // ==========================================
      // LOGIC FORK 2: STANDARD CLIENT -> DIRECT FTL PROVISIONING
      // ==========================================
      else {
        const clientPayload = {
          ...basePayload,
          stage: 'newLeads',
          status: 'pre_launch',
          paymentStatus: 'pending_ftl'
        };

        await createTenantLead(tenantId, clientPayload);

        // Execute remote provisioning to build DB and send SendGrid FTL link
        const provisionTenantAccountFn = httpsCallable(functions, 'provisionTenantAccount');
        await provisionTenantAccountFn({
          email: formData.contactEmail,
          contactName: formData.contactName,
          companyName: formData.entityName,
          tenantId: tenantId, 
          targetTenantId: pendingTenantId,
          packageType: formData.packageType,
          mrr: calculatedMrrValue
        });

        setSuccessType('PROVISIONED');
      }

    } catch (error) {
      console.error("Deal Provisioning Failed:", error);
      alert("System Error: Could not dispatch contract to the database. Check console.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const wrapperClass = isOperatorView 
    ? "w-full min-h-[80vh] flex flex-col animate-in fade-in duration-500 pb-12" 
    : "fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm animate-in fade-in";

  const containerClass = isOperatorView
    ? "w-full max-w-5xl mx-auto bg-white border border-slate-300 shadow-sm flex flex-col mt-4" 
    : "w-full max-w-3xl bg-slate-50 h-full border-l border-slate-300 shadow-2xl flex flex-col slide-in-from-right duration-300";

  // ==========================================
  // SUCCESS SCREENS
  // ==========================================
  if (successType) {
    if (successType === 'DEAL_DESK') {
      return (
        <div className="flex items-center justify-center h-[70vh] animate-in fade-in duration-500">
          <div className="bg-white p-12 border border-slate-300 shadow-xl max-w-lg w-full text-center rounded-sm">
            <div className="w-20 h-20 bg-indigo-50 text-indigo-600 border border-indigo-200 flex items-center justify-center mx-auto mb-6 rounded-full">
              <i className="fa-solid fa-scale-balanced text-4xl"></i>
            </div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-widest mb-2">Deal Submitted</h2>
            <p className="text-sm text-slate-500 font-medium mb-8 leading-relaxed">
              This Master Agent package has been successfully routed to the <strong>Deal Desk</strong>. Please upload their signed NDA and EIN verification letter to their profile for final Founder approval.
            </p>
            <button onClick={handleClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold py-4 uppercase tracking-widest shadow-sm transition-colors rounded-sm flex items-center justify-center gap-2">
              Return to Workspace
            </button>
          </div>
        </div>
      );
    }

    if (successType === 'PROVISIONED') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white p-12 border border-slate-300 shadow-2xl max-w-lg w-full text-center animate-in zoom-in-95">
            <div className="w-20 h-20 bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto mb-6 rounded-full">
              <i className="fa-solid fa-paper-plane text-4xl"></i>
            </div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-widest mb-2">Client Provisioned</h2>
            <p className="text-sm text-slate-500 font-medium mb-2">
              The tenant environment has been successfully mapped.
            </p>
            <p className="text-xs font-bold text-slate-400 mb-8 border border-slate-200 bg-slate-50 py-3 rounded-sm">
              <i className="fa-solid fa-envelope text-emerald-500 mr-2"></i> FTL Gateway Link Dispatched to Client
            </p>
            <button onClick={handleClose} className="w-full bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-4 uppercase tracking-widest shadow-sm transition-colors">
              Return to Workspace
            </button>
          </div>
        </div>
      );
    }
  }

  return (
    <div className={wrapperClass}>
      <div className={containerClass}>
        
        <div className="bg-slate-900 p-6 border-b border-slate-700 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-black text-white uppercase tracking-tight">Enterprise Deal Engine</h2>
            <p className={`text-[10px] font-bold uppercase tracking-widest mt-1 ${isOperatorView ? 'text-emerald-500' : 'text-amber-500'}`}>
              {isOperatorView ? 'Operator POS Terminal' : 'Founder Mode: Master Provisioning'}
            </p>
          </div>
          {!isOperatorView && (
            <button onClick={handleClose} className="text-slate-400 hover:text-white transition-colors">
              <i className="fa-solid fa-xmark text-xl"></i>
            </button>
          )}
        </div>

        <form id="deal-engine-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-10 bg-slate-50">
          
          {/* SECTION 1: LEGAL ENTITY */}
          <div className="bg-white border border-slate-200 p-6 shadow-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
               <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">1</span> Legal Entity Information
            </h3>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Legal Corporate Name <span className="text-red-500">*</span></label>
                <input required type="text" name="entityName" value={formData.entityName} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="e.g., Summit Retail LLC" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">DBA (Optional)</label>
                <input type="text" name="dba" value={formData.dba} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="e.g., Summit Wireless" />
              </div>

              <div className="col-span-2 grid grid-cols-12 gap-4">
                <div className="col-span-12">
                   <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Street Address <span className="text-red-500">*</span></label>
                   <input required type="text" name="billingStreet" value={formData.billingStreet} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="123 Corporate Blvd, Suite 100" />
                </div>
                <div className="col-span-6 md:col-span-5">
                   <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">City <span className="text-red-500">*</span></label>
                   <input required type="text" name="billingCity" value={formData.billingCity} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="Seattle" />
                </div>
                <div className="col-span-3 md:col-span-4">
                   <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">State <span className="text-red-500">*</span></label>
                   <select required name="billingState" value={formData.billingState} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-2 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer">
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                   </select>
                </div>
                <div className="col-span-3 md:col-span-3">
                   <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">ZIP <span className="text-red-500">*</span></label>
                   <input required type="text" name="billingZip" value={formData.billingZip} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="98101" />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Corporate EIN / Tax ID <span className="text-red-500">*</span></label>
                <input required type="text" name="ein" value={formData.ein} onChange={handleChange} maxLength="10" className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none tracking-widest" placeholder="XX-XXXXXXX" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Operating Role <span className="text-red-500">*</span></label>
                <select required name="entityRole" value={formData.entityRole} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer">
                  <option value="">Select Role...</option>
                  <option value="Authorized Retailer">Authorized Retailer</option>
                  <option value="Master Agent">Master Agent / Dealer</option>
                  <option value="Independent Operator">Independent Operator</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Primary Vertical <span className="text-red-500">*</span></label>
                <select required name="industryType" value={formData.industryType} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer">
                  <option value="">Select Vertical...</option>
                  <option value="Wireless Retail">Wireless Retail</option>
                  <option value="Automotive / Mechanic">Automotive / Mechanic Shop</option>
                  <option value="Floral / Plant Shop">Floral / Plant Shop</option>
                  <option value="Restaurant / Food Service">Restaurant / Food Service</option>
                  <option value="Other">Other (Specify below)</option>
                </select>
                {formData.industryType === 'Other' && (
                  <input required type="text" name="customIndustryType" value={formData.customIndustryType} onChange={handleChange} className="w-full bg-amber-50 border border-amber-300 px-4 py-2.5 text-sm font-bold text-slate-800 mt-2 focus:outline-none" placeholder="Type custom industry..." />
                )}
              </div>
            </div>
          </div>

          {/* SECTION 2: STAKEHOLDERS */}
          <div className="bg-white border border-slate-200 p-6 shadow-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">2</span> Stakeholder Matrix
            </h3>
            <div className="grid grid-cols-2 gap-5 mb-5 border-b border-slate-100 pb-5">
              <div className="col-span-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-[-10px]">Primary Signatory (MSA Execution)</div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Full Name <span className="text-red-500">*</span></label>
                <input required type="text" name="contactName" value={formData.contactName} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Corporate Title <span className="text-red-500">*</span></label>
                <input required type="text" name="contactTitle" value={formData.contactTitle} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Signatory Email <span className="text-red-500">*</span></label>
                <input required type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Direct Phone <span className="text-red-500">*</span></label>
                <input required type="tel" name="contactPhone" value={formData.contactPhone} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div className="col-span-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-[-10px]">Operational Contacts (Optional)</div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">A/P Billing Email</label>
                <input type="email" name="billingEmail" value={formData.billingEmail} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="Accounts Payable..." />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Technical/IT Email</label>
                <input type="email" name="techEmail" value={formData.techEmail} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="For system handoff..." />
              </div>
            </div>
          </div>

          {/* SECTION 3: MIGRATION DATA */}
          <div className="bg-white border border-slate-200 p-6 shadow-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">3</span> Deployment Context
            </h3>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Legacy System (Optional)</label>
                <input type="text" name="legacySystem" value={formData.legacySystem} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" placeholder="e.g., RQ4, Square, Clover..." />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Target Go-Live Date <span className="text-red-500">*</span></label>
                <input required type="date" name="goLiveDate" value={formData.goLiveDate} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none" />
              </div>
            </div>
          </div>

          {/* SECTION 4: PACKAGING & PRICING */}
          <div className="bg-white border border-slate-200 p-6 shadow-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-500 font-bold text-[9px]">4</span> Apex Retail Suite Provisioning
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <label className={`relative block cursor-pointer border-2 p-5 transition-all bg-white ${formData.packageType === 'operations' ? 'border-slate-800 shadow-md' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="packageType" value="operations" checked={formData.packageType === 'operations'} onChange={handleChange} className="absolute opacity-0" />
                <div className="flex justify-between items-start mb-2">
                  <span className={`text-sm font-black uppercase ${formData.packageType === 'operations' ? 'text-slate-800' : 'text-slate-500'}`}>Operations Hub</span>
                  {formData.packageType === 'operations' && <i className="fa-solid fa-circle-check text-slate-800 text-xl"></i>}
                </div>
                <p className="text-2xl font-black text-slate-900 mb-4">${OPERATIONS_HUB_PRICE}<span className="text-[10px] font-bold text-slate-500 uppercase ml-1">/ month</span></p>
                <ul className="space-y-2">
                  <li className="text-xs font-bold text-slate-600"><i className="fa-solid fa-check text-emerald-500 mr-2"></i>Company Branded Intranet</li>
                  <li className="text-xs font-bold text-slate-600"><i className="fa-solid fa-check text-emerald-500 mr-2"></i>Employee Scheduling Tool</li>
                  <li className="text-xs font-bold text-slate-600"><i className="fa-solid fa-check text-emerald-500 mr-2"></i>Store Calendar & Sync</li>
                  <li className="text-xs font-bold text-slate-600"><i className="fa-solid fa-check text-emerald-500 mr-2"></i>Store Visit Checklist Tool</li>
                  <li className="text-xs font-bold text-slate-600"><i className="fa-solid fa-check text-emerald-500 mr-2"></i>SOP Management Library</li>
                  <li className="text-xs font-bold text-slate-600"><i className="fa-solid fa-check text-emerald-500 mr-2"></i>Manager Weekly Checklist</li>
                </ul>
              </label>

              <label className={`relative block cursor-pointer border-2 p-5 transition-all ${formData.packageType === 'apex_os' ? 'border-[#0f172a] bg-[#0f172a] shadow-xl' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                <input type="radio" name="packageType" value="apex_os" checked={formData.packageType === 'apex_os'} onChange={handleChange} className="absolute opacity-0" />
                
                {formData.packageType === 'apex_os' && <div className="absolute top-0 right-0 bg-amber-400 text-amber-900 text-[8px] font-black uppercase tracking-widest px-2 py-1 transform translate-x-2 -translate-y-2">Flagship Standard</div>}

                <div className="flex justify-between items-start mb-2">
                  <span className={`text-sm font-black uppercase ${formData.packageType === 'apex_os' ? 'text-white' : 'text-slate-800'}`}>Apex-OS Enterprise Stack</span>
                  {formData.packageType === 'apex_os' && <i className="fa-solid fa-circle-check text-amber-400 text-xl"></i>}
                </div>
                <p className={`text-2xl font-black mb-4 ${formData.packageType === 'apex_os' ? 'text-white' : 'text-slate-900'}`}>${APEX_SUITE_PRICE}<span className={`text-[10px] font-bold uppercase ml-1 ${formData.packageType === 'apex_os' ? 'text-slate-400' : 'text-slate-500'}`}>/ month</span></p>
                
                <p className={`text-[9px] font-bold uppercase tracking-widest mb-3 pb-2 border-b ${formData.packageType === 'apex_os' ? 'text-slate-300 border-slate-700' : 'text-slate-500 border-slate-200'}`}>Standard Stack Inclusions:</p>
                <ul className="space-y-2">
                  <li className={`text-xs font-bold ${formData.packageType === 'apex_os' ? 'text-slate-300' : 'text-slate-600'}`}>
                    <i className={`fa-solid fa-check mr-2 ${formData.packageType === 'apex_os' ? 'text-amber-400' : 'text-emerald-500'}`}></i>Base Corporate Door License (${BASE_DOOR_FEE}/mo)
                  </li>
                  <li className={`text-xs font-bold ${formData.packageType === 'apex_os' ? 'text-slate-300' : 'text-slate-600'}`}>
                    <i className={`fa-solid fa-check mr-2 ${formData.packageType === 'apex_os' ? 'text-amber-400' : 'text-emerald-500'}`}></i>CheckHQ Payroll Engine Base (${CHECKHQ_BASE_FEE}/mo)
                  </li>
                  <li className={`text-xs font-bold ${formData.packageType === 'apex_os' ? 'text-slate-300' : 'text-slate-600'}`}>
                    <i className={`fa-solid fa-check mr-2 ${formData.packageType === 'apex_os' ? 'text-amber-400' : 'text-emerald-500'}`}></i>1 Initial Administrator Seat (${INITIAL_SEAT_FEE}/mo)
                  </li>
                  <li className={`text-xs font-bold ${formData.packageType === 'apex_os' ? 'text-slate-300' : 'text-slate-600'}`}>
                    <i className={`fa-solid fa-check mr-2 ${formData.packageType === 'apex_os' ? 'text-amber-400' : 'text-emerald-500'}`}></i>Apex-OS POS & Retail Suite
                  </li>
                  <li className={`text-xs font-bold ${formData.packageType === 'apex_os' ? 'text-slate-300' : 'text-slate-600'}`}>
                    <i className={`fa-solid fa-check mr-2 ${formData.packageType === 'apex_os' ? 'text-amber-400' : 'text-emerald-500'}`}></i>Real-time Telemetry Map
                  </li>
                </ul>
              </label>
            </div>

            <div className="bg-slate-900 text-white p-6 shadow-inner flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex-1 max-w-xs w-full">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Override MRR (Rep Negotiation)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                  <input 
                    type="number" 
                    name="customMrrOverride" 
                    placeholder={formData.packageType === 'apex_os' ? APEX_SUITE_PRICE.toString() : OPERATIONS_HUB_PRICE.toString()}
                    value={formData.customMrrOverride} 
                    onChange={handleChange} 
                    className="w-full bg-slate-800 border border-slate-700 text-white pl-8 pr-2 py-2.5 text-sm font-bold focus:outline-none focus:border-emerald-500 transition-colors" 
                  />
                </div>
              </div>

              <div className="flex justify-end items-center gap-6">
                <div className="text-right">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Due Today</p>
                  <p className="text-xl font-black text-slate-500">$0 <span className="text-[10px] uppercase font-bold">(Billed at FTL)</span></p>
                </div>
                <div className="w-px h-10 bg-slate-700 hidden sm:block"></div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-1">Calculated MRR</p>
                  <p className="text-3xl font-black text-emerald-400">${calculateMrr()}<span className="text-sm text-slate-400 font-medium">/mo</span></p>
                </div>
              </div>
            </div>
          </div>

        </form>

        <div className="p-6 border-t border-slate-300 bg-slate-100 flex items-center justify-between gap-4">
          <label className="flex items-center gap-3 cursor-pointer flex-1">
            <input type="checkbox" name="isConfirmed" checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} className="w-5 h-5 text-emerald-600 focus:ring-emerald-500 border-slate-300 rounded" />
            <span className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">I confirm that all corporate, operational, and billing details above are accurate.</span>
          </label>
          <div className="flex gap-4">
            <button onClick={handleClose} type="button" className="px-8 py-3 text-[10px] font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 uppercase tracking-widest transition-colors shadow-sm">
              Cancel
            </button>
            <button 
              type="submit"
              form="deal-engine-form"
              disabled={isSubmitting || tenantLoading || !isConfirmed}
              className={`px-8 py-3 text-[10px] font-bold text-white uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 ${
                formData.entityRole === 'Master Agent' 
                  ? 'bg-indigo-600 hover:bg-indigo-700 border border-indigo-700' 
                  : 'bg-emerald-600 hover:bg-emerald-700 border border-emerald-700'
              }`}
            >
              {tenantLoading ? 'Authenticating...' : (
                isSubmitting ? <><i className="fa-solid fa-spinner fa-spin"></i> Transmitting...</> : (
                  formData.entityRole === 'Master Agent' 
                    ? <><i className="fa-solid fa-paper-plane"></i> Send to Deal Desk</> 
                    : <><i className="fa-solid fa-bolt"></i> Provision Client & Send FTL</>
                )
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}